import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, PlayCircle, FilePlus, ArrowLeft, BookOpen, Loader2, Pen, Trash2, Printer } from 'lucide-react';
import ErrorNotePrint from '../WrongAnswer/ErrorNotePrint';
import ErrnotePrintOverlay from '../WrongAnswer/ErrnotePrintOverlay';
import { Button, IconButton } from '../common/Button';
import { useAuth } from '../../context/AuthContext';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../utils/latexParser';
import { fetchMathConcepts as fetchMathConceptsUtil } from '../../utils/mathConceptsUtils';
import { MathceptDetailModal, useMathceptDetail } from '../common';
import { DIFFICULTY_COLORS } from '../../pages/LearningStatus/utils/constants';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 난이도별 색상 (Tailwind 클래스)
const getDifficultyColorClass = (difficulty) => {
  const colors = {
    1: 'bg-green-100', 2: 'bg-blue-100', 3: 'bg-yellow-100',
    4: 'bg-orange-100', 5: 'bg-red-100',
  };
  return colors[difficulty] || 'bg-gray-100';
};

// 난이도별 색상 (rgba)
const getDifficultyColor = (difficulty) => {
  return DIFFICULTY_COLORS[difficulty - 1] || 'rgba(156, 163, 175, 0.7)';
};

// 개념 타입별 색상
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)', 'AXM': 'rgba(249, 115, 22, 0.5)',
    'IDN': 'rgba(245, 158, 11, 0.5)', 'THM': 'rgba(34, 197, 94, 0.5)',
    'MTH': 'rgba(6, 182, 212, 0.5)', 'PRP': 'rgba(59, 130, 246, 0.5)',
    'FOR': 'rgba(139, 92, 246, 0.5)', 'NTN': 'rgba(236, 72, 153, 0.5)'
  };
  return colors[type] || 'rgba(251, 146, 60, 0.5)';
};

function ExamListModal({ isOpen, onClose, examList, onSelectExam, stampId, onWrongAnswerGenerated, onExamCreated, onRefreshExamList }) {
  const { getAccessToken } = useAuth();
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'preview' | 'reviews'
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'reviews' — 탭 상태 (preview와 분리)
  const [selectedExamIds, setSelectedExamIds] = useState(new Set());
  const [isExtracting, setIsExtracting] = useState(false);

  // 문제 미리보기 상태
  const [problems, setProblems] = useState([]); // { problem_id, difficulty, address }[]
  const [previewData, setPreviewData] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [selectedProblemIds, setSelectedProblemIds] = useState(new Set());
  const [mathConcepts, setMathConcepts] = useState([]);

  // 오답노트 리스트 상태
  const [reviewList, setReviewList] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  // 오답노트 삭제 상태
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetReviewId, setDeleteTargetReviewId] = useState(null);

  // 오답노트 메모 수정 상태
  const [memoModalOpen, setMemoModalOpen] = useState(false);
  const [editingMemo, setEditingMemo] = useState({ content_id: null, content_type: 'review', memo: '' });

  // 오답노트 행 선택(슬라이드 액션) 상태
  const [activeReviewId, setActiveReviewId] = useState(null);

  // Epsilon 출제 상태
  const [isCreatingExam, setIsCreatingExam] = useState(false);

  // 오답노트 출력 모달 상태
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printNoteId, setPrintNoteId] = useState(null);
  const [printScale, setPrintScale] = useState(0.7);
  const [printAddEmptyPage, setPrintAddEmptyPage] = useState(false);
  const [printFunction, setPrintFunction] = useState(null);
  const [showV4Print, setShowV4Print] = useState(false); // 신규 인쇄(v4) 오버레이
  const [epsilonConfirmOpen, setEpsilonConfirmOpen] = useState(false);
  const [epsilonTargetNoteId, setEpsilonTargetNoteId] = useState(null);

  // 매스컨셉 상세 모달
  const { isModalOpen: mathceptDetailModalOpen, loading: mathceptDetailLoading, data: mathceptDetailData, fetchAndShow: fetchMathceptDetails, closeModal: closeMathceptDetailModal } = useMathceptDetail({ handleAuth: false });

  // 모달이 열릴 때 시험 목록 새로고침
  useEffect(() => {
    if (isOpen && onRefreshExamList) {
      onRefreshExamList();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setViewMode('list');
    setActiveTab('list');
    setSelectedExamIds(new Set());
    setProblems([]);
    setPreviewData([]);
    setSelectedProblemIds(new Set());
    onClose();
  };

  // 오답노트 리스트 조회
  const fetchReviewList = async () => {
    if (!stampId) return;
    setIsLoadingReviews(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/exams/reviews?stamp_id=${stampId}`,
        { headers: { 'Authorization': token } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setReviewList(data.data || []);
    } catch (error) {
      console.error('오답노트 목록 조회 오류:', error);
      setReviewList([]);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // 오답노트 삭제
  const handleDeleteReview = async () => {
    if (!deleteTargetReviewId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/exams/reviews?review_id=${deleteTargetReviewId}`,
        { method: 'DELETE', headers: { 'Authorization': token } }
      );
      if (response.status === 404) { alert('해당 리뷰를 찾을 수 없습니다.'); return; }
      if (!response.ok) throw new Error('삭제 중 오류가 발생했습니다.');
      alert('성공적으로 삭제되었습니다.');
      fetchReviewList();
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제에 실패했습니다.');
    } finally {
      setDeleteModalOpen(false);
      setDeleteTargetReviewId(null);
    }
  };

  // 오답노트 메모 수정
  const handleUpdateMemo = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/exams/reviews`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({
            memo: editingMemo.memo,
            content_id: editingMemo.content_id,
            content_type: editingMemo.content_type
          })
        }
      );
      if (response.status === 404) { alert('해당 항목을 찾을 수 없습니다.'); return; }
      if (!response.ok) throw new Error('메모 수정 중 오류가 발생했습니다.');
      alert('메모가 수정되었습니다.');
      fetchReviewList();
    } catch (error) {
      console.error('메모 수정 오류:', error);
      alert('메모 수정에 실패했습니다.');
    } finally {
      setMemoModalOpen(false);
      setEditingMemo({ content_id: null, content_type: 'review', memo: '' });
    }
  };

  // 오답노트 출력 모달 열기
  const handleOpenPrintModal = (noteId) => {
    if (!noteId) return;
    setPrintNoteId(noteId);
    setPrintFunction(null);
    setShowPrintModal(true);
  };

  // 오답노트 출력 모달 닫기
  const handleClosePrintModal = () => {
    setShowPrintModal(false);
    setPrintNoteId(null);
    setPrintFunction(null);
    setPrintScale(0.7);
    setPrintAddEmptyPage(false);
  };

  // 오답노트 인쇄 실행
  const handleExecutePrint = async () => {
    if (!printNoteId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/exams/reviews/print`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({ content_type: 'errnote', content_id: printNoteId })
        }
      );
      if (!response.ok) throw new Error('인쇄 기록 생성 실패');
      if (printFunction) printFunction();
    } catch (error) {
      console.error('인쇄 오류:', error);
      alert('인쇄 처리 중 오류가 발생했습니다.');
    }
  };

  // 스케일 조절
  const handlePrintScaleChange = (delta) => {
    setPrintScale(prev => Math.max(0.2, Math.min(1.2, +(prev + delta).toFixed(1))));
  };

  // Epsilon 출제
  const handleCreateEpsilonExam = async (noteId) => {
    if (!stampId || !noteId || isCreatingExam) return;
    setIsCreatingExam(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/exams/${stampId}/epsilon`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({
            error_note_id: noteId
          })
        }
      );
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 409) {
          alert(`시험 생성 실패: ${data.message || '충돌이 발생했습니다.'}`);
        } else if (response.status === 422) {
          alert(`출제할 문제가 없습니다. ${data.message || ''}`);
        } else {
          alert(`오류: ${data.message || '시험 생성에 실패했습니다.'}`);
        }
        return;
      }
      alert('시험이 생성되었습니다.');
      if (onExamCreated) onExamCreated();
    } catch (error) {
      console.error('Epsilon 출제 오류:', error);
      alert('시험 생성 중 오류가 발생했습니다.');
    } finally {
      setIsCreatingExam(false);
    }
  };

  // 탭 전환 핸들러
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'reviews') {
      setViewMode('reviews');
      fetchReviewList();
    } else {
      setViewMode('list');
    }
  };

  const isMobileView = window.innerWidth < 768;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    date.setHours(date.getHours() + 9);
    return date.toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  // 체크박스 토글
  const toggleExamSelection = (examId) => {
    setSelectedExamIds(prev => {
      const next = new Set(prev);
      if (next.has(examId)) next.delete(examId);
      else next.add(examId);
      return next;
    });
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedExamIds.size === examList.length) {
      setSelectedExamIds(new Set());
    } else {
      setSelectedExamIds(new Set(examList.map(e => e.exam_id)));
    }
  };

  // 오답 문제 추출
  const handleExtractWrongAnswers = async () => {
    if (selectedExamIds.size === 0) return;

    setIsExtracting(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/customexams/temp/errnote?exam_ids=${[...selectedExamIds].join(',')}`,
        { headers: { 'Authorization': token } }
      );

      if (response.status === 204) {
        alert('선택한 시험들에서 오답을 찾을 수 없습니다.');
        return;
      }
      if (response.status === 422) {
        alert('오답 문제가 100개를 초과합니다.');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const failedProbs = data.data.failed_probs;
      const problemIds = failedProbs.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));

      // 문제 목록 구성
      const problemsList = problemIds.map(id => ({ problem_id: id }));
      setProblems(problemsList);
      setSelectedProblemIds(new Set(problemIds));
      setViewMode('preview');
    } catch (error) {
      console.error('오답 수집 중 오류:', error);
      alert('오답 수집 중 오류가 발생했습니다.');
    } finally {
      setIsExtracting(false);
    }
  };

  // 시험 목록 뷰
  const renderListView = () => (
    <>
      {/* 바디 */}
      <div className="flex-1 overflow-y-auto px-4">
        <table className="w-full table-fixed">
          <colgroup><col style={{ width: '5%' }} /><col style={{ width: '18%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '15%' }} /><col style={{ width: '17%' }} /><col style={{ width: '25%' }} /></colgroup>
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-1.5 py-2 text-center">
                <input
                  type="checkbox"
                  checked={selectedExamIds.size === examList.length && examList.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-primary rounded focus:ring-primary cursor-pointer"
                />
              </th>
              <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">상태/TID</th>
              <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">난이도</th>
              <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">문제수</th>
              <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">정답률</th>
              <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                <span className="text-xs md:text-sm">상태</span>
              </th>
              <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">일시</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {examList.map((exam, index) => {
              const examTypeSymbol = exam.exam_type === 'alpha' ? 'α' :
                exam.exam_type === 'beta' ? 'β' :
                exam.exam_type === 'gamma' ? 'γ' :
                exam.exam_type === 'delta' ? 'δ' :
                exam.exam_type === 'custom' ? '𝐂' :
                exam.exam_type === 'epsilon' ? 'ε' :
                exam.exam_type || '-';
              const totalCount = exam.problems_count || ((exam.correct_count || 0) + (exam.wrong_count || 0));
              const correctRate = totalCount > 0
                ? Math.round(((exam.correct_count || 0) / totalCount) * 100)
                : null;
              const isChecked = selectedExamIds.has(exam.exam_id);

              return (
                <tr
                  key={exam.exam_id}
                  onClick={() => onSelectExam(exam.exam_id, index)}
                  className="hover:bg-primary/10 cursor-pointer transition-colors duration-150 border-b border-gray-100"
                >
                  {/* 체크박스 */}
                  <td className="px-1.5 py-2 text-center rounded-l-3xl transition-colors duration-150">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => { e.stopPropagation(); toggleExamSelection(exam.exam_id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-primary rounded focus:ring-primary cursor-pointer"
                    />
                  </td>
                  {/* 상태/TID */}
                  <td className="px-1.5 py-2 overflow-hidden text-center transition-colors duration-150">
                    <div className="flex items-center justify-center gap-1">
                      <div className="flex-shrink-0">
                        {exam.exam_status === 999 ? (
                          <CheckCircle2 className="text-green-600" size={24} />
                        ) : exam.exam_status === 400 ? (
                          <PlayCircle className="text-orange-500" size={24} />
                        ) : (
                          <FilePlus className="text-blue-500" size={24} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-0.5">
                          <span className="text-sm font-semibold text-gray-800">{exam.exam_id}</span>
                          <span className="text-sm font-medium text-green-600">{examTypeSymbol}</span>
                        </div>
                        {exam.exam_round != null && <div className="text-xs text-gray-500">({exam.exam_round}회)</div>}
                      </div>
                    </div>
                  </td>
                  {/* 난이도 */}
                  <td className="px-1.5 py-2 text-center transition-colors duration-150">
                    <span className="text-sm font-semibold text-gray-800">{exam.exam_difficulty || '-'}</span>
                  </td>
                  {/* 문제수 */}
                  <td className="px-1.5 py-2 text-center transition-colors duration-150">
                    <span className="text-sm font-semibold text-gray-800">{totalCount}</span>
                  </td>
                  {/* 정답률 */}
                  <td className="px-1.5 py-2 text-center transition-colors duration-150">
                    <div className="text-sm font-semibold text-green-600">
                      {correctRate !== null ? `${correctRate}%` : '-'}
                    </div>
                    <div className="text-[10px]">
                      <span className="text-primary font-bold">{exam.correct_count || 0}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-danger font-bold">{exam.wrong_count || 0}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-black font-bold">{totalCount}</span>
                    </div>
                  </td>
                  {/* 상태 */}
                  <td className="px-1.5 py-2 text-center transition-colors duration-150">
                    <span className={`text-xs md:text-sm font-semibold whitespace-nowrap ${
                      exam.exam_status === 999 ? 'text-green-600' :
                      exam.exam_status === 400 ? 'text-orange-500' :
                      exam.exam_status === 200 ? 'text-blue-500' : 'text-gray-500'
                    }`}>
                      {exam.exam_status === 200 ? "결과입력필요" :
                       exam.exam_status === 400 ? "결과입력완료" :
                       exam.exam_status === 999 ? "결과확정완료" : "상태 미정"}
                    </span>
                  </td>
                  {/* 일시 */}
                  <td className="px-1.5 py-2 overflow-hidden rounded-r-3xl transition-colors duration-150">
                    <div className="text-xs text-gray-500">
                      <div className="truncate">생성: {exam.exam_created_at ? formatDate(exam.exam_created_at) : '-'}</div>
                      <div className="truncate">수정: {exam.exam_updated_at ? formatDate(exam.exam_updated_at) : '-'}</div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 푸터 */}
      <div className="flex shrink-0 items-center justify-between border-t p-4">
        <Button
          variant="primary"
          onClick={handleExtractWrongAnswers}
          disabled={selectedExamIds.size === 0 || isExtracting}
          className="flex items-center gap-2"
        >
          {isExtracting && <Loader2 className="w-4 h-4 animate-spin" />}
          오답 노트 {selectedExamIds.size > 0 && `(${selectedExamIds.size}건)`}
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          닫기
        </Button>
      </div>
    </>
  );

  // 오답노트 리스트 뷰
  const renderReviewsView = () => (
    <>
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4">
        {isLoadingReviews ? (
          <div className="flex items-center justify-center py-12">
            <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          </div>
        ) : reviewList.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            오답노트가 없습니다.
          </div>
        ) : (
          <div className="overflow-visible">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">No.</th>
                  <th className="px-2 py-3 text-left text-xs font-medium tracking-wider text-gray-500">작성자</th>
                  <th className="px-2 py-3 text-left text-xs font-medium tracking-wider text-gray-500">TID(회차)</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">메모</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">오답</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">전체</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">생성일</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {reviewList.map((item) => {
                  const isActive = activeReviewId === item.review_id;
                  return (
                    <tr
                      key={item.seq}
                      className={`relative cursor-pointer transition-colors duration-150 border-b border-gray-100 hover:bg-primary/10 ${
                        isActive ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => setActiveReviewId(isActive ? null : item.review_id)}
                    >
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500"><span className="inline-grid size-5 place-items-center rounded-full border border-primary text-[11px] font-bold text-primary">{item.seq}</span></td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-900">{item.author}</td>
                      <td className="px-2 py-3 text-sm text-gray-900 overflow-hidden">
                        <div
                          className={isActive ? 'break-words whitespace-normal select-text cursor-text' : 'truncate select-none'}
                          onClick={isActive ? (e) => e.stopPropagation() : undefined}
                          title={item.exam_ids?.map((id, index) => `${id}(${item.exam_rounds?.[index]})`).join(', ')}
                        >
                          {item.exam_ids?.map((id, index) => (
                            <span key={id}>
                              {id}({item.exam_rounds?.[index]})
                              {index < item.exam_ids.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-900 overflow-hidden">
                        <div className={`flex items-center gap-1 ${isActive ? 'flex-wrap' : ''}`}>
                          <span className={isActive ? 'break-words whitespace-normal' : 'truncate'} title={item.memo || ''}>
                            {item.memo || '-'}
                          </span>
                          <IconButton
                            variant="ghost"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingMemo({
                                content_id: item.review_id,
                                content_type: 'review',
                                memo: item.memo || ''
                              });
                              setMemoModalOpen(true);
                            }}
                            className="p-1 text-gray-500 hover:text-gray-700 shrink-0"
                          >
                            <Pen className="size-3.5" />
                          </IconButton>
                        </div>
                      </td>
                      <td className={`${isActive ? '' : 'whitespace-nowrap'} px-2 py-3 text-sm text-gray-900`}>{item.wrong_problems}</td>
                      <td className={`${isActive ? '' : 'whitespace-nowrap'} px-2 py-3 text-sm text-gray-900`}>{item.total_problems}</td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-900 relative overflow-visible">
                        {new Date(item.created_at).toLocaleDateString('ko-KR')}
                        {/* 슬라이딩 액션 버튼 */}
                        <div
                          className={`absolute inset-y-0 right-0 flex items-center gap-2 pr-2 bg-gradient-to-l from-primary/10 via-primary/10 to-transparent pl-8 transition-all duration-300 ease-out ${
                            isActive ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                          }`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTargetReviewId(item.review_id);
                              setDeleteModalOpen(true);
                            }}
                            className="flex items-center justify-center bg-red-50 text-red-600 hover:bg-red-100 shadow-sm !p-0"
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={!item.errnotes?.length}
                            onClick={(e) => {
                              e.stopPropagation();
                              const noteId = item.errnotes?.[0]?.note_id;
                              if (noteId) handleOpenPrintModal(noteId);
                            }}
                            className="flex items-center justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 shadow-sm !p-0"
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                          >
                            <Printer className="size-3.5" />
                          </Button>
                          <button
                            disabled={!item.errnotes?.length || isCreatingExam}
                            onClick={(e) => {
                              e.stopPropagation();
                              const noteId = item.errnotes?.[0]?.note_id;
                              if (noteId) {
                                setEpsilonTargetNoteId(noteId);
                                setEpsilonConfirmOpen(true);
                              }
                            }}
                            className={`rounded-full select-none transition-all duration-200 ${
                              !item.errnotes?.length || isCreatingExam
                                ? 'opacity-40 cursor-not-allowed'
                                : 'cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-md active:scale-90 active:ring-4 active:ring-primary/70 active:ring-offset-2 active:ring-offset-white'
                            }`}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              border: 'none',
                              outline: 'none',
                              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0) 68%, rgba(255,255,255,0.92) 74%, rgba(255,255,255,0) 82%), radial-gradient(circle at 50% 52%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.12) 88%, rgba(0,0,0,0) 96%)',
                              backgroundColor: 'rgba(255,255,255,0.85)',
                              backdropFilter: 'blur(8px)',
                              WebkitBackdropFilter: 'blur(8px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08), inset 0 0 0 2px rgba(255,255,255,0.12)',
                            }}
                            title="출제"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* 푸터 */}
      <div className="flex shrink-0 items-center justify-end border-t p-4">
        <Button variant="secondary" onClick={handleClose}>
          닫기
        </Button>
      </div>

      {/* 오답노트 출제 확인 모달 */}
      {epsilonConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999990, backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="rounded-3xl bg-white p-6 shadow-xl" style={{ maxWidth: '400px', width: '90%' }}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">오답노트 출제</h3>
            <p className="text-sm text-gray-600 mb-6">오답노트에서 시험을 출제하시겠습니까?</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setEpsilonConfirmOpen(false); setEpsilonTargetNoteId(null); }}>
                취소
              </Button>
              <Button onClick={() => { setEpsilonConfirmOpen(false); handleCreateEpsilonExam(epsilonTargetNoteId); setEpsilonTargetNoteId(null); }}>
                출제
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999990, backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="rounded-3xl bg-white p-6 shadow-xl" style={{ maxWidth: '400px', width: '90%' }}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">오답노트 삭제</h3>
            <p className="text-sm text-gray-600 mb-6">정말 삭제하시겠습니까? 삭제된 오답노트는 복구할 수 없습니다.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setDeleteModalOpen(false); setDeleteTargetReviewId(null); }}>
                취소
              </Button>
              <Button variant="danger" onClick={handleDeleteReview}>
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 메모 수정 모달 */}
      {memoModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999990, backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="rounded-3xl bg-white p-6 shadow-xl" style={{ maxWidth: '400px', width: '90%' }}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">메모 수정</h3>
            <textarea
              className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              rows={4}
              value={editingMemo.memo}
              onChange={(e) => setEditingMemo(prev => ({ ...prev, memo: e.target.value }))}
              placeholder="메모를 입력하세요"
              maxLength={500}
            />
            <div className="text-xs text-gray-400 text-right mt-1">{editingMemo.memo.length}/500</div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => { setMemoModalOpen(false); setEditingMemo({ content_id: null, content_type: 'review', memo: '' }); }}>
                취소
              </Button>
              <Button variant="primary" onClick={handleUpdateMemo}>
                저장
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // 문제 미리보기 뷰
  const renderPreviewView = () => (
    <WrongAnswerPreview
      problems={problems}
      previewData={previewData}
      setPreviewData={setPreviewData}
      isLoadingPreview={isLoadingPreview}
      setIsLoadingPreview={setIsLoadingPreview}
      selectedProblemIds={selectedProblemIds}
      setSelectedProblemIds={setSelectedProblemIds}
      mathConcepts={mathConcepts}
      setMathConcepts={setMathConcepts}
      getAccessToken={getAccessToken}
      onBack={() => { setViewMode('list'); setActiveTab('list'); }}
      onClose={handleClose}
      onNavigateToReviews={async () => {
        // 1. 시험 목록 갱신 (ExamResult 쪽)
        if (onWrongAnswerGenerated) await onWrongAnswerGenerated();
        // 2. 먼저 리스트를 fetch한 후 뷰 전환 (뷰 전환하면 Preview가 언마운트되므로 순서 중요)
        await fetchReviewList();
        setActiveTab('reviews');
        setViewMode('reviews');
      }}
      fetchMathceptDetails={fetchMathceptDetails}
      stampId={stampId}
      selectedExamIds={selectedExamIds}
    />
  );

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center transition-all"
      style={{
        zIndex: 9999980,
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
        backgroundColor: 'rgba(0, 0, 0, 0.3)'
      }}
      onClick={handleBackgroundClick}
    >
      <div
        className="flex flex-col rounded-3xl bg-white shadow-xl transition-all w-[95vw] lg:w-[1000px]"
        style={{
          height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex shrink-0 flex-col border-b">
          <div className="flex items-center justify-between p-4 pb-0">
            <div className="flex items-center gap-2">
              {viewMode === 'preview' && (
                <IconButton
                  onClick={() => { setViewMode('list'); setActiveTab('list'); }}
                  variant="ghost"
                  size="sm"
                  aria-label="돌아가기"
                >
                  <ArrowLeft size={20} />
                </IconButton>
              )}
              <h2 className="text-xl font-bold text-gray-800">
                {viewMode === 'preview' ? `오답 문제 미리보기 (${problems.length}문제)` : '테스트 관리'}
              </h2>
            </div>
            <IconButton onClick={handleClose} variant="ghost" size="sm" className="ml-2" aria-label="닫기">
              <X size={20} />
            </IconButton>
          </div>
          {/* 탭 (preview 모드가 아닐 때만 표시) */}
          {viewMode !== 'preview' && (
            <div className="flex pt-2">
              <button
                className={`flex-1 text-center px-4 py-2 text-sm border-b-2 transition-colors ${
                  activeTab === 'list'
                    ? 'border-primary text-primary font-extrabold'
                    : 'border-transparent text-gray-500 font-medium hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => handleTabChange('list')}
              >
                Ky Test {examList.length > 0 && `(${examList.length})`}
              </button>
              <button
                className={`flex-1 text-center px-4 py-2 text-sm border-b-2 transition-colors ${
                  activeTab === 'reviews'
                    ? 'border-primary text-primary font-extrabold'
                    : 'border-transparent text-gray-500 font-medium hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => handleTabChange('reviews')}
              >
                오답노트 {reviewList.length > 0 && `(${reviewList.length})`}
              </button>
            </div>
          )}
        </div>

        {viewMode === 'list' && renderListView()}
        {viewMode === 'preview' && renderPreviewView()}
        {viewMode === 'reviews' && renderReviewsView()}
      </div>

      {/* 매스컨셉 상세 모달 */}
      <MathceptDetailModal
        isOpen={mathceptDetailModalOpen}
        onClose={closeMathceptDetailModal}
        data={mathceptDetailData}
        loading={mathceptDetailLoading}
        renderLatex={(text) => {
          if (!text) return '';
          try {
            return text.replace(/\$([^$]+)\$/g, (match, formula) => {
              try { return katex.renderToString(formula, { throwOnError: false, displayMode: false }); }
              catch (e) { return match; }
            });
          } catch (error) { return text; }
        }}
        showCloseButton={false}
        useLoader={false}
        handleAuth={false}
      />

      {/* 오답노트 출력 미리보기 모달 */}
      {showPrintModal && printNoteId && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 9999985, backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={handleClosePrintModal}
        >
          <div
            className="flex flex-col rounded-3xl bg-white shadow-xl transition-all w-[95vw] lg:w-[1000px]"
            style={{
              height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 rounded-t-3xl border-b bg-white px-4 py-3">
              <h2 className="text-lg font-bold text-gray-800">오답노트 출력 미리보기</h2>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* 연습장 토글 */}
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printAddEmptyPage}
                    onChange={(e) => setPrintAddEmptyPage(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-xs text-gray-600">연습장</span>
                </label>
                {/* 스케일 조절 */}
                <div className="flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1">
                  <button
                    onClick={() => handlePrintScaleChange(-0.1)}
                    className="px-1.5 py-0.5 text-sm font-bold text-gray-600 hover:text-gray-900"
                  >−</button>
                  <span className="min-w-[3ch] text-center text-xs font-medium text-gray-700">
                    {Math.round(printScale * 100)}%
                  </span>
                  <button
                    onClick={() => handlePrintScaleChange(0.1)}
                    className="px-1.5 py-0.5 text-sm font-bold text-gray-600 hover:text-gray-900"
                  >+</button>
                </div>
                {/* 인쇄 버튼 */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleExecutePrint}
                  disabled={!printFunction}
                  className="flex items-center gap-1"
                >
                  <Printer className="size-4" />
                  인쇄
                </Button>
                {/* 신규 인쇄(v4) — 구형과 병행. 데이터는 어댑터가 따로 받아 v4 규격으로 조판한다 */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowV4Print(true)}
                  className="flex items-center gap-1"
                >
                  <Printer className="size-4" />
                  신규 인쇄
                </Button>
                {/* 닫기 */}
                <IconButton onClick={handleClosePrintModal} variant="ghost" size="sm" aria-label="닫기">
                  <X size={18} />
                </IconButton>
              </div>
            </div>
            {/* 본문 - 미리보기 */}
            <div className="flex-1 min-h-0 overflow-auto rounded-b-3xl bg-gray-200 p-4">
              <ErrorNotePrint
                noteId={printNoteId}
                mode="preview"
                scale={printScale}
                onPrint={setPrintFunction}
                addEmptyPage={printAddEmptyPage}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 오답노트 신규 인쇄(v4) — 인쇄 미리보기 모달 헤더의 「신규 인쇄」 버튼이 연다 */}
      <ErrnotePrintOverlay
        noteId={printNoteId}
        open={showV4Print && !!printNoteId}
        onClose={() => setShowV4Print(false)}
      />
    </div>,
    document.body
  );
}

// 오답 문제 미리보기 하위 컴포넌트
function WrongAnswerPreview({
  problems, previewData, setPreviewData,
  isLoadingPreview, setIsLoadingPreview,
  mathConcepts, setMathConcepts,
  getAccessToken, onBack, onNavigateToReviews, fetchMathceptDetails,
  stampId, selectedExamIds
}) {
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const editorRef = useRef(null);

  const getEditor = useCallback(() => {
    if (!editorRef.current) {
      editorRef.current = createEditor({ nodes: [...PlaygroundNodes] });
    }
    return editorRef.current;
  }, []);

  const fetchWithTokenRefresh = useCallback(async (url) => {
    const token = await getAccessToken();
    if (!token) throw new Error('인증 토큰이 없습니다.');
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error('API 요청 실패');
    return await response.json();
  }, [getAccessToken]);

  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = getEditor();
    const processNode = (node) => {
      node = { ...node };
      if (node.type === 'equation') node.equation = processMathEquation(node.equation);
      else if (node.type === 'box-content') node.className = 'PlaygroundEditorTheme__boxContent';
      if (node.children && Array.isArray(node.children)) node.children = node.children.map(child => processNode(child));
      return node;
    };
    const data = { ...lexicalData, root: processNode(lexicalData.root) };
    const editorState = editor.parseEditorState(data);
    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => { content = $generateHtmlFromNodes(editor); });
    return content;
  }, [getEditor]);

  const renderLatex = (text) => {
    if (!text) return '';
    try {
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try { return katex.renderToString(formula, { throwOnError: false, displayMode: false }); }
        catch (e) { return match; }
      });
    } catch (error) { return text; }
  };

  // 문제 콘텐츠 로드
  useEffect(() => {
    if (problems.length === 0) return;
    let isMounted = true;

    const loadPreviewData = async () => {
      setIsLoadingPreview(true);
      try {
        let currentMathConcepts = mathConcepts;
        if (currentMathConcepts.length === 0) {
          try {
            const concepts = await fetchMathConceptsUtil();
            if (isMounted) setMathConcepts(concepts);
            currentMathConcepts = concepts;
          } catch (error) { console.error('Math concepts 로드 실패:', error); }
        }

        const problemIds = problems.map(p => p.problem_id);
        const BATCH_SIZE = 50;
        const batches = [];
        for (let i = 0; i < problemIds.length; i += BATCH_SIZE) {
          batches.push(problemIds.slice(i, i + BATCH_SIZE));
        }

        const batchResults = await Promise.all(
          batches.map(async (batch) => {
            const ids = batch.join(',');
            const [printRes, mathceptsRes] = await Promise.all([
              fetchWithTokenRefresh(`/api/problems/print/page?problem_ids=${ids}&page=1&per_page=${BATCH_SIZE}`),
              fetchWithTokenRefresh(`/api/mathcepts/temp/by-prob-ids?problem_ids=${ids}`).catch(() => null),
            ]);
            return { printRes, mathceptsRes };
          })
        );

        if (!isMounted) return;

        const contentMap = {};
        batchResults.forEach(({ printRes }) => {
          if (printRes?.status === 'success' && printRes.data) {
            printRes.data.forEach(item => {
              try { contentMap[item.problem_id] = convertLexicalToHtml(item.lexical_data.lexical_data); }
              catch (e) { contentMap[item.problem_id] = null; }
            });
          }
        });

        const conceptDataMap = {};
        batchResults.forEach(({ mathceptsRes }) => {
          if (mathceptsRes?.status === 'success' && mathceptsRes?.data) {
            mathceptsRes.data.forEach(item => {
              conceptDataMap[item.problem_id] = { concept_ids: item.concept_ids || [], difficulty: item.difficulty };
            });
          }
        });

        const previews = problems.map(p => {
          const conceptData = conceptDataMap[p.problem_id] || {};
          const conceptIds = conceptData.concept_ids || [];
          const matchedConcepts = {};
          conceptIds.forEach(cid => {
            const mc = currentMathConcepts.find(c => c.id === cid || String(c.id) === String(cid));
            if (mc) {
              matchedConcepts[cid] = {
                concept_name: mc.concept_name || mc.text,
                order: mc.order,
                concept_type: mc.concept_type,
              };
            }
          });
          return {
            problem_id: p.problem_id,
            difficulty: conceptData.difficulty || p.difficulty,
            address: p.address,
            content: contentMap[p.problem_id] || null,
            matchedConcepts,
          };
        });

        if (isMounted) setPreviewData(previews);
      } catch (error) {
        console.error('문제 미리보기 로드 실패:', error);
      } finally {
        if (isMounted) setIsLoadingPreview(false);
      }
    };

    loadPreviewData();
    return () => { isMounted = false; };
  }, [problems]);

  // 난이도별 분포 (전체 문제 기준)
  const difficultyDistribution = useMemo(() => {
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    problems.forEach(p => {
      const preview = previewData.find(pd => pd.problem_id === p.problem_id);
      const diff = preview?.difficulty || p.difficulty;
      if (diff && dist.hasOwnProperty(diff)) dist[diff]++;
    });
    return dist;
  }, [problems, previewData]);

  return (
    <>
      {/* 바디 */}
      <div className="flex-1 p-4 overflow-hidden flex flex-col">
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* 난이도 분포 */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">난이도 분포</h4>
              <span className="text-sm font-medium text-gray-700">
                난이도 평균 {problems.length > 0
                  ? (problems.reduce((sum, p) => {
                      const preview = previewData.find(pd => pd.problem_id === p.problem_id);
                      return sum + (preview?.difficulty || p.difficulty || 0);
                    }, 0) / problems.length).toFixed(2)
                  : '-'}
              </span>
            </div>
            <div className="space-y-1">
              {[1, 2, 3, 4, 5].map((level) => {
                const count = difficultyDistribution[level];
                const maxCount = Math.max(...Object.values(difficultyDistribution), 1);
                const percent = (count / maxCount) * 100;
                return (
                  <div key={level} className="flex items-center gap-2">
                    <span className="text-xs w-8">Lv.{level}</span>
                    <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                      <div className={`h-full ${getDifficultyColorClass(level)}`} style={{ width: `${percent}%` }} />
                    </div>
                    <span className="text-xs w-12 text-right">{count}문제</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 문제 미리보기 */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center gap-3 mb-2 shrink-0">
              <span className="text-sm font-medium text-gray-700">
                오답 문제 ({problems.length}문제)
              </span>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 ai-test-preview-scroll" style={{ scrollbarWidth: 'thin' }}>
              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
                </div>
              ) : problems.length > 0 ? (
                <div className="space-y-3">
                  {problems.map((problem) => {
                    const preview = previewData.find(pd => pd.problem_id === problem.problem_id);

                    return (
                      <div
                        key={problem.problem_id}
                        className="rounded-3xl border border-gray-200 bg-white p-3 shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20"
                      >
                        {/* 상단 */}
                        <div className="mb-1 flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">PID: {problem.problem_id}</span>
                            {(preview?.difficulty || problem.difficulty) && (
                              <div
                                className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                                style={{ backgroundColor: getDifficultyColor(preview?.difficulty || problem.difficulty) }}
                              >
                                Lv.{preview?.difficulty || problem.difficulty}
                              </div>
                            )}
                          </div>
                          {(preview?.address || problem.address) && (
                            <span className="text-xs text-gray-500">{preview?.address || problem.address}</span>
                          )}
                        </div>

                        {/* 수학 개념 */}
                        {preview?.matchedConcepts && Object.keys(preview.matchedConcepts).length > 0 && (
                          <div className="mb-3 rounded-lg bg-gray-50 p-2">
                            <div className="flex items-center gap-2 mb-1">
                              <BookOpen className="w-4 h-4 text-gray-600" />
                              <span className="text-xs font-medium text-gray-600">수학 개념</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(preview.matchedConcepts).map(([id, matched]) => (
                                <div
                                  key={id}
                                  className="flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-white transition-colors"
                                  onClick={(e) => { e.stopPropagation(); fetchMathceptDetails(id); }}
                                  title="클릭하여 상세 정보 보기"
                                >
                                  {matched?.order && (
                                    <span className="inline-flex items-center justify-center font-semibold text-gray-700" style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}>
                                      {matched.order}
                                    </span>
                                  )}
                                  {matched?.concept_type && (
                                    <span className="inline-flex items-center justify-center font-semibold text-gray-800" style={{ backgroundColor: getConceptTypeColor(matched.concept_type), borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '8px' }}>
                                      {matched.concept_type}
                                    </span>
                                  )}
                                  <span dangerouslySetInnerHTML={{ __html: renderLatex(matched.concept_name) }} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 문제 콘텐츠 */}
                        <div className="problem-content-wrapper">
                          {preview?.content ? (
                            <div className="problem-content" dangerouslySetInnerHTML={{ __html: preview.content }} />
                          ) : (
                            !isLoadingPreview && (
                              <div className="text-xs text-gray-400 py-2">콘텐츠를 불러올 수 없습니다.</div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">추출된 오답 문제가 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 푸터 */}
      <div className="flex shrink-0 items-center justify-between border-t p-4">
        <Button variant="secondary" onClick={onBack} className="flex items-center gap-1">
          <ArrowLeft size={16} />
          시험 목록
        </Button>
        <Button
          variant="primary"
          disabled={isCreatingReview || !stampId || selectedExamIds.size === 0}
          onClick={async () => {
            if (!stampId || selectedExamIds.size === 0) return;
            setIsCreatingReview(true);
            try {
              const token = await getAccessToken();
              const response = await fetch(
                `${API_BASE_URL}/api/exams/reviews`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                  },
                  body: JSON.stringify({
                    stamp_id: parseInt(stampId),
                    exam_ids: [...selectedExamIds].join(',')
                  })
                }
              );
              const data = await response.json();
              // body의 status/status_code로 실제 결과 판단 (HTTP 200이어도 body에 에러가 올 수 있음)
              if (data.status === 'error' || data.status_code === 422 || data.status_code === 409 || !response.ok) {
                if (data.status_code === 422) {
                  alert('선택한 시험에 오답이 없어 오답노트를 생성할 수 없습니다.');
                } else if (data.status_code === 409) {
                  alert('이미 존재하는 오답노트입니다. 오답노트 탭에서 확인하세요.');
                  if (onNavigateToReviews) await onNavigateToReviews();
                } else if (response.status === 403) {
                  alert('권한이 없습니다.');
                } else {
                  alert(`오답노트 생성 중 오류가 발생했습니다. (${data.message || ''})`);
                }
                return;
              }
              alert('오답노트가 생성되었습니다.');
              if (onNavigateToReviews) await onNavigateToReviews();
            } catch (error) {
              console.error('오답노트 생성 중 오류:', error);
              alert('오답노트 생성 중 오류가 발생했습니다.');
            } finally {
              setIsCreatingReview(false);
            }
          }}
          className="flex items-center gap-2"
        >
          {isCreatingReview && <Loader2 className="w-4 h-4 animate-spin" />}
          오답노트 생성
        </Button>
      </div>

      <style>{`
        .problem-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 500 !important;
          font-size: 18px !important;
        }
        .katex { font-size: 1em !important; }
        .PlaygroundEditorTheme__boxContent {
          border: 1px solid black; padding: 10px; margin: 10px auto;
          max-width: 100%; border-radius: 4px; background-color: #ffffff;
        }
        .problem-content-wrapper { display: flex; align-items: flex-start; width: 100%; }
        .problem-content { overflow-wrap: break-word; word-break: break-word; }
        .ai-test-preview-scroll::-webkit-scrollbar { width: 4px; }
        .ai-test-preview-scroll::-webkit-scrollbar-track { background: transparent; }
        .ai-test-preview-scroll::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.3); border-radius: 2px; }
        .ai-test-preview-scroll::-webkit-scrollbar-thumb:hover { background: rgba(156, 163, 175, 0.5); }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideInRight { animation: slideInRight 0.25s ease-out forwards; }
      `}</style>
    </>
  );
}

export default ExamListModal;
